import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import ApiClient from "#/redux/api/ApiClient.js";
import { getErrorMessage } from "#/utils/APIUtils.js";

export const getTemplates = createAsyncThunk(
  "templates/getPage",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        "/api/docx/gen/docxTemplate/page",
        null,
        page,
        filter,
        sort
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getTemplateById = createAsyncThunk(
  "templates/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get("/api/docx/gen/docxTemplate/" + id);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getTemplateBySifra = createAsyncThunk(
  "templates/getBySifra",
  async (sifra, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `/api/docx/gen/docxTemplate/bysifra/${sifra}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const downloadTemplate = createAsyncThunk(
  "templates/download",
  async (id, { rejectWithValue }) => {
    try {
      const response = await ApiClient.getFile(
        "/api/docx/gen/docxTemplate/templatedownload/" +
          id +
          `?templateId=${id}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getPlaceholders = createAsyncThunk(
  "templates/placeholders/getPage",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        "/api/docx/gen/docxPlaceholder/page",
        null,
        page,
        filter,
        sort
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getPlaceholderById = createAsyncThunk(
  "templates/placeholders/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `/api/docx/gen/docxPlaceholder/${id}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const createTemplate = createAsyncThunk(
  "templates/create",
  async ({ dto }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post("/api/docx/gen/docxTemplate", dto);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateTemplate = createAsyncThunk(
  "templates/update",
  async ({ id, dto }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.put(
        `/api/docx/gen/docxTemplate/${id}`,
        dto
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const createPlaceholder = createAsyncThunk(
  "templates/placeholders/create",
  async ({ dto }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post(
        "/api/docx/gen/docxPlaceholder",
        dto
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updatePlaceholder = createAsyncThunk(
  "templates/placeholders/update",
  async ({ id, dto }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.put(
        `/api/docx/gen/docxPlaceholder/${id}`,
        dto
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const deleteTemplate = createAsyncThunk(
  "templates/delete",
  async ({ id, version }, { rejectWithValue }) => {
    try {
      await ApiClient.delete(
        `/api/docx/gen/docxTemplate/${id}?version=${version}`
      );
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const deletePlaceholder = createAsyncThunk(
  "templates/placeholders/delete",
  async ({ id, version }, { rejectWithValue }) => {
    try {
      await ApiClient.delete(
        `/api/docx/gen/docxPlaceholder/${id}?version=${version}`
      );
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const templateSlice = createSlice({
  name: "template",
  initialState: {
    templates: {
      isLoading: false,
      error: null,
      page: {},
      byCode: {},
      details: null,
    },
    placeholders: {
      isLoading: false,
      error: null,
      page: {},
      details: null,
    },
    templatesForKey: {
      isLoading: false,
      error: null,
      templates: null,
    },
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getTemplates.fulfilled, (state, action) => {
        state.templates.isLoading = false;
        state.templates.page = action.payload;
        action.payload.content.forEach((template) => {
          state.templates.byCode[template.sifra] = template;
        });
      })
      .addCase(getTemplates.rejected, (state, action) => {
        state.templates.isLoading = false;
        state.templates.error = action.payload;
      })
      .addCase(getTemplates.pending, (state) => {
        state.templates.isLoading = true;
        state.templates.error = null;
      })

      .addCase(getTemplateById.fulfilled, (state, action) => {
        state.templates.isLoading = false;
        state.templates.details = action.payload;
      })
      .addCase(getTemplateById.rejected, (state, action) => {
        state.templates.isLoading = false;
        state.templates.error = action.payload;
      })
      .addCase(getTemplateById.pending, (state) => {
        state.templates.isLoading = true;
        state.templates.error = null;
      })

      .addCase(getPlaceholderById.fulfilled, (state, action) => {
        state.placeholders.isLoading = false;
        state.placeholders.details = action.payload;
      })
      .addCase(getPlaceholderById.rejected, (state, action) => {
        state.placeholders.isLoading = false;
        state.placeholders.error = action.payload;
      })
      .addCase(getPlaceholderById.pending, (state) => {
        state.placeholders.isLoading = true;
        state.placeholders.error = null;
      })

      .addCase(getPlaceholders.fulfilled, (state, action) => {
        state.placeholders.isLoading = false;
        state.placeholders.page = action.payload;
      })
      .addCase(getPlaceholders.rejected, (state, action) => {
        state.placeholders.isLoading = false;
        state.placeholders.error = action.payload;
      })
      .addCase(getPlaceholders.pending, (state) => {
        state.placeholders.isLoading = true;
        state.placeholders.error = null;
      })

      .addCase(createTemplate.fulfilled, (state, _) => {
        state.templates.isLoading = false;
      })
      .addCase(createTemplate.rejected, (state, action) => {
        state.templates.isLoading = false;
        state.templates.error = action.payload;
      })
      .addCase(createTemplate.pending, (state) => {
        state.templates.isLoading = true;
        state.templates.error = null;
      })

      .addCase(updateTemplate.fulfilled, (state, _) => {
        state.templates.isLoading = false;
      })
      .addCase(updateTemplate.rejected, (state, action) => {
        state.templates.isLoading = false;
        state.templates.error = action.payload;
      })
      .addCase(updateTemplate.pending, (state) => {
        state.templates.isLoading = true;
        state.templates.error = null;
      })

      .addCase(createPlaceholder.fulfilled, (state, _) => {
        state.placeholders.isLoading = false;
      })
      .addCase(createPlaceholder.rejected, (state, action) => {
        state.placeholders.isLoading = false;
        state.placeholders.error = action.payload;
      })
      .addCase(createPlaceholder.pending, (state) => {
        state.placeholders.isLoading = true;
        state.placeholders.error = null;
      })

      .addCase(updatePlaceholder.fulfilled, (state, _) => {
        state.placeholders.isLoading = false;
      })
      .addCase(updatePlaceholder.rejected, (state, action) => {
        state.placeholders.isLoading = false;
        state.placeholders.error = action.payload;
      })
      .addCase(updatePlaceholder.pending, (state) => {
        state.placeholders.isLoading = true;
        state.placeholders.error = null;
      })

      /* Connect .addCase for the bySifra connect to templatesForKey values  */
      .addCase(getTemplateBySifra.fulfilled, (state, action) => {
        state.templatesForKey.isLoading = false;
        state.templatesForKey.templates = action.payload;
      })
      .addCase(getTemplateBySifra.rejected, (state, action) => {
        state.templatesForKey.isLoading = false;
        state.templatesForKey.error = action.payload;
      })
      .addCase(getTemplateBySifra.pending, (state) => {
        state.templatesForKey.isLoading = true;
        state.templatesForKey.error = null;
      });
  },
});

export default templateSlice.reducer;
